import { Box, Chip, useMediaQuery, useTheme } from '@mui/material';
import MUIDataTable, { MUIDataTableColumnDef, MUIDataTableOptions } from 'mui-datatables';
import { FunctionComponent, useCallback, useContext, useEffect, useState } from 'react';
import { ViewHeader } from '../../../core/components/ViewHeader/ViewHeader';
import { AppContext } from '../../../core/config';
import { useAppTranslate, useBusinessContext } from '../../../core/hooks';
import { Loader } from '../../../ui/components';
import { L3ServicesBusinessContextConfig } from '../../config/L3ServicesBusinessContext.config';
import { useL3ServicesApiGet } from '../../hooks';
import { EventRegistrationScope } from '../../model';
import { L3ServiceMobile } from '../L3ServiceMobile/L3ServiceMobile';
import { L3ServicePreference } from '../L3ServicePreference/L3ServicePreference';
import { L3ServiceStatusIcon } from '../L3ServiceStatusIcon/L3ServiceStatusIcon';
import { parseUpdateEvent, updateServices } from './L3ServiceList.utils';
import { useNetworkServiceEventsApi } from '../../api/NetworkServiceEvents/NetworkServiceEvents.api';

const INITIAL_ROWS_PER_PAGE = 10;
const EVENT_REGISTRATION_TRIGGERS = ['changePage', 'changeRowsPerPage', 'sort'];

export const L3ServiceList: FunctionComponent = () => {
  const { customerId } = useContext(AppContext);
  const { services, isFetching, isFetched } = useL3ServicesApiGet(customerId);
  const { sendJsonMessage, lastMessage, isConnected } = useNetworkServiceEventsApi();
  const theme = useTheme();
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('md'));
  const translate = useAppTranslate();
  const [forcedRowsPerPage, setForcedRowsPerPage] = useState<number|undefined>(INITIAL_ROWS_PER_PAGE);
  const [displayedItemIds, setDisplayedItemIds] = useState<number[]|null>(null);

  const clearForcedRowsPerPage = (): void => setForcedRowsPerPage(undefined);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  let allServices = services || [];

  const getEffectivelyDisplayedItemIds = useCallback((): number[] => {
    if (services === null) {
      return [];
    } else if (displayedItemIds === null) {
      return services
          .slice(0, INITIAL_ROWS_PER_PAGE)
          .map(l3Service => l3Service.itemId);
    } else {
      return displayedItemIds;
    }
  }, [services, displayedItemIds]);

  const registerDisplayedServicesObservation = useCallback((): void => {
    const registrationEvents = getEffectivelyDisplayedItemIds().map(itemId => ({
      networkServiceId: itemId,
      eventRegistrationScopes: [
        EventRegistrationScope.Maintenance,
        EventRegistrationScope.Status,
        EventRegistrationScope.Notification,
      ]
    }));
    if (registrationEvents.length > 0) {
      sendJsonMessage({ eventRegistrations: registrationEvents });
    }
  }, [sendJsonMessage, getEffectivelyDisplayedItemIds]);

  useBusinessContext(L3ServicesBusinessContextConfig);
  
  useEffect(() => {
    if (!isFetching && isFetched && isConnected) {
      registerDisplayedServicesObservation();
    }
  }, [isFetched, isFetching, customerId, isConnected, registerDisplayedServicesObservation]);

  useEffect(() => {
    if (lastMessage !== null) {
      const events = parseUpdateEvent(lastMessage.data);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      allServices = updateServices(allServices, events);
    }
  }, [lastMessage]);

  const columns: MUIDataTableColumnDef[] = isTabletOrMobile ? [
    {
      label: ' ',
      name: 'itemId',
      options: {
        customHeadRender: () => null,
      },
    },
    {
      label: ' ',
      name: 'orderNumber',
      options: {
        customHeadRender: () => null,
      },
    },
    {
      label: ' ',
      name: 'address',
      options: {
        customHeadRender: () => null,
      },
    },
    {
      label: ' ',
      name: 'name',
      options: {
        customHeadRender: () => null,
      },
    },
  ] : [
    {
      name: 'itemId',
      options: {
        display: false
      }
    },
    {
      label: translate('tableHeaders.name'),
      name: 'name',
    },
    {
      label: translate('tableHeaders.status'),
      name: 'status',
      options: {
        customBodyRenderLite: (dataIndex) => {
          return <L3ServiceStatusIcon l3Service={allServices[dataIndex]} />;
        },
      },
    },
    {
      label: translate('tableHeaders.soz'),
      name: 'orderNumber',
    },
    {
      label: translate('tableHeaders.location'),
      name: 'address',
    },
    {
      label: translate('tableHeaders.deviceType'),
      name: 'device.type',
    },
    {
      label: translate('tableHeaders.throughput'),
      name: 'throughputFormatted',
    },
    {
      label: translate('tableHeaders.notifications'),
      name: 'notifications',
      options: {
        customBodyRenderLite: (dataIndex) => {
          return <Chip
            label={allServices[dataIndex].notificationsQuantity}
            sx={{ background: '#D7EDFF', fontWeight: 'bold' }}
          />;
        },
      },
    },
    {
      label: translate('tableHeaders.preference'),
      name: 'preference',
      options: {
        customBodyRenderLite: (dataIndex) => {
          return <L3ServicePreference l3Service={allServices[dataIndex]} />;
        },
      },
    },
  ];

  const options: MUIDataTableOptions = {
    enableNestedDataAccess: '.',
    rowsPerPage: forcedRowsPerPage,
    rowsPerPageOptions: [5, 10, 25, 50],
    sort: true,
    selectToolbarPlacement: 'none',
    download: false,
    filter: false,
    print: false,
    viewColumns: false,
    customToolbarSelect: () => null,
    rowHover: false,
    expandableRows: false,
    selectableRows: 'none',
    responsive: 'simple',
    searchPlaceholder: translate('searchService'),
    customToolbar: () => null,
    onChangeRowsPerPage: (event) => {
      clearForcedRowsPerPage();
    },
    onTableChange(action, tableState) {
      const pageIndex = tableState.page;
      const pageSize = tableState.rowsPerPage;
      if (EVENT_REGISTRATION_TRIGGERS.includes(action)) {
        const startIndex = pageIndex * pageSize;
        const tableData = tableState.data;
        const endIndex = Math.min(startIndex + pageSize, tableData.length);
        const displayedItemIds = tableData
          .slice(startIndex, endIndex)
          .map(dataElement => dataElement.data[0])
        setDisplayedItemIds(displayedItemIds);
      }
    },
    customRowRender: isTabletOrMobile
      ? (data, dataIndex, rowIndex) => {
        const service = allServices[dataIndex];
        return <L3ServiceMobile
          key={dataIndex}
          l3Service={service}
        />;
      }
      : undefined,
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2 }}>
      <ViewHeader title={translate('title')} />
      <Loader loading={isFetching} />
      {isFetched && !isFetching && allServices && (
        <MUIDataTable
          title={''}
          columns={columns}
          data={allServices}
          options={options}
        />
      )}
    </Box>
  );
};